// Growem — local date helpers. Day boundaries follow America/Bogota (UTC-5, no DST) so an
// evening Barranquilla session never rolls into the next calendar day.

import { MONTHS, MONTHS_FULL, DOWS } from '../data/plan'

const TZ = 'America/Bogota'
const isoFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
})

/** Local ISO "YYYY-MM-DD" for a given instant (defaults to now), in Colombia time. */
export function localISO(d: Date = new Date()): string {
  return isoFmt.format(d) // en-CA → YYYY-MM-DD
}

export function todayKey(): string {
  return localISO()
}

/** Parse "YYYY-MM-DD" to a local Date at midnight (safe for getDay/formatting). */
export function parseDay(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** Day of week 0=Sunday … 6=Saturday for an ISO day. */
export function dow(iso: string): number {
  return parseDay(iso).getDay()
}

/** Plan index for a date: Mon..Sat → 0..5; Sunday → null (rest). */
export function planIndexForDow(d: number): number | null {
  return d === 0 ? null : d - 1
}

export function isRestDay(iso: string, restDow = 0): boolean {
  return dow(iso) === restDow
}

/** "lun 27 jul" */
export function dateLabel(iso: string): string {
  const dt = parseDay(iso)
  return `${DOWS[dt.getDay()].slice(0, 3)} ${dt.getDate()} ${MONTHS[dt.getMonth()]}`
}

export function dateNum(iso: string): number {
  return parseDay(iso).getDate()
}

/** "julio 2026" */
export function monthTitle(year: number, month: number): string {
  return `${MONTHS_FULL[month]} ${year}`
}

export function hhmm(d: Date = new Date()): string {
  return d.toLocaleTimeString('es-CO', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false })
}

/** Local hour (0–23) in Colombia time — for protein-debt / coach timing. */
export function localHour(d: Date = new Date()): number {
  return Number(new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', hour12: false }).format(d))
}

export function addDays(iso: string, n: number): string {
  const dt = parseDay(iso)
  dt.setDate(dt.getDate() + n)
  return localISO(dt)
}

/** Whole-day difference a - b (both ISO). */
export function diffDays(a: string, b: string): number {
  const ms = parseDay(a).getTime() - parseDay(b).getTime()
  return Math.round(ms / 86400000)
}

/**
 * Calendar grid for a month, Monday-first. Returns 42 cells (6 weeks) with the day's ISO
 * and whether it belongs to the shown month.
 */
export function monthGrid(year: number, month: number): { iso: string; inMonth: boolean }[] {
  const first = new Date(year, month, 1)
  const jsDow = first.getDay()            // 0=Sun
  const lead = (jsDow + 6) % 7            // Monday-first offset
  const start = new Date(year, month, 1 - lead)
  const cells: { iso: string; inMonth: boolean }[] = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    cells.push({ iso: localISO(d), inMonth: d.getMonth() === month })
  }
  return cells
}
